import ApiService from './api.service'
import { FileDownloadService } from '@/services/file-download.service'
import { buildDownloadTimestamp } from '@/helpers/fileDownload'
import { applyShopUserStatsExportFileName, getFilenameFromContentDisposition } from '@/helpers/shopUserStatsExport'
import store from '@/store/index'

/** @typedef {'ready' | 'running' | 'failed' | 'missing'} ShopUserStatsExportStatus */

/**
 * @typedef {Object} ShopUserStatsExportStatusResponse
 * @property {string} tenant
 * @property {string | null} latest_generated_at
 * @property {number | null} latest_rows
 * @property {boolean} refresh_running
 * @property {ShopUserStatsExportStatus | string} refresh_status
 * @property {string | null} last_point_created_at
 * @property {boolean} is_stale
 * @property {boolean} can_download_now
 * @property {boolean} should_wait_for_next
 * @property {string} next_refresh_eta
 * @property {number} refresh_interval_minutes
 * @property {string} message
 * @property {boolean} success
 */

/**
 * @typedef {Object} ShopUserStatsExportErrorResponse
 * @property {string} message
 * @property {'failed' | 'running' | 'missing'} status
 * @property {boolean} success
 * @property {boolean} [refresh_running]
 */

const DEFAULT_EXPORT_FILENAME = 'bsplus-shop-user'

const unwrapExportStatusPayload = (response) => {
  const body = response?.data
  if (!body) {
    return null
  }

  if (typeof body.success === 'boolean' || body.can_download_now !== undefined) {
    return body
  }

  const nested = body.data
  if (nested && (typeof nested.success === 'boolean' || nested.can_download_now !== undefined)) {
    return nested
  }

  return body
}

const parseBlobJsonError = async (response) => {
  if (!response?.data || !(response.data instanceof Blob)) {
    return null
  }

  const contentType = response.headers?.['content-type'] || ''
  if (!contentType.includes('application/json') && response.status !== 409 && response.status !== 404) {
    return null
  }

  try {
    const text = await response.data.text()
    return JSON.parse(text)
  } catch {
    return null
  }
}

const StatService = {
  async trainingResults(data = { page: 1, training_id: null, user_id: null }) {
    try {
      return await ApiService.get(
        `trainings-results?page=${data.page}${data.training_id ? '&training_id=' + data.training_id : ''}${
          data.user_id ? '&user_id=' + data.user_id : ''
        }`
      )
    } catch (error) {
      return error
    }
  },

  async bTrainingResults(page = 1) {
    try {
      return await ApiService.get('btrainings-results?page=' + page)
    } catch (error) {
      return error
    }
  },

  async externalTrainingResults(page = 1) {
    try {
      return await ApiService.get('external-trainings-results?page=' + page)
    } catch (error) {
      return error
    }
  },

  async exportTrainingResults() {
    return await ApiService.getFile('trainings-results/export')
  },

  async investmentResults(page = 1) {
    try {
      return await ApiService.get('investment-results?page=' + page)
    } catch (error) {
      return error
    }
  },

  async hunterResults(page = 1) {
    try {
      return await ApiService.get('hunter-results?page=' + page)
    } catch (error) {
      return error
    }
  },

  async uberallResults(page = 1) {
    try {
      return await ApiService.get('uberall-results?page=' + page)
    } catch (error) {
      return error
    }
  },

  async requestCounts() {
    try {
      return await ApiService.get('request-count-stats')
    } catch (error) {
      return error
    }
  },

  async getIsNewOrderItems() {
    try {
      return await ApiService.get('is-new-order-items')
    } catch (error) {
      return error
    }
  },

  async testEmail() {
    try {
      return await ApiService.post('admin/test/mail')
    } catch (error) {
      return error
    }
  },

  /**
   * @returns {Promise<ShopUserStatsExportStatusResponse>}
   */
  async getShopUserStatsExportStatus() {
    const response = await ApiService.get('shop-user-stats/export/status')
    const payload = unwrapExportStatusPayload(response)

    if (!payload) {
      throw new Error('Invalid export status response')
    }

    return payload
  },

  /**
   * @returns {Promise<{ success: true, fileName: string }>}
   */
  async downloadShopUserStatsExportLatest(format = 'xlsx') {
    try {
      const response = await ApiService.getFile('shop-user-stats/export/download?mode=latest')
      const contentType = response.headers?.['content-type'] || ''

      if (contentType.includes('application/json')) {
        const err = await parseBlobJsonError(response)
        throw Object.assign(new Error(err?.message || 'Download failed'), {
          status: err?.status,
          refresh_running: err?.refresh_running,
          httpStatus: response.status
        })
      }

      const rawFileName =
        getFilenameFromContentDisposition(response.headers?.['content-disposition']) || DEFAULT_EXPORT_FILENAME
      const fileName = applyShopUserStatsExportFileName(
        rawFileName,
        store.getters.tenantId,
        window.location.hostname,
        buildDownloadTimestamp()
      )

      const downloadResult = await FileDownloadService.downloadExcelFromCsvBlob(response.data, fileName, format)

      if (!downloadResult?.success) {
        throw new Error(downloadResult?.message || 'Failed to convert export to Excel')
      }

      return { success: true, fileName: downloadResult.fileName }
    } catch (error) {
      const response = error?.response
      const parsed = response ? await parseBlobJsonError(response) : null
      const httpStatus = response?.status

      throw Object.assign(new Error(parsed?.message || error?.response?.data?.message || error?.message || 'Download failed'), {
        status: parsed?.status,
        refresh_running: parsed?.refresh_running,
        httpStatus
      })
    }
  },

  async downloadUserShopStats(step, options = {}) {
    try {
      const query = new URLSearchParams({
        async: '1',
        step
      })

      if (options.export_token) {
        query.set('export_token', String(options.export_token))
      }

      if (step === 'prime' || step === 'prepare') {
        if (options.initialize !== undefined && options.initialize !== null) {
          query.set('initialize', options.initialize ? '1' : '0')
        }
      }

      if (step === 'prepare') {
        if (options.cursor !== undefined && options.cursor !== null) {
          query.set('cursor', String(options.cursor))
        }

        if (options.chunk_size !== undefined && options.chunk_size !== null) {
          query.set('chunk_size', String(options.chunk_size))
        }
      }

      if (step === 'export') {
        if (options.substep) {
          query.set('substep', String(options.substep))
        }

        if (options.filename) {
          query.set('filename', String(options.filename))
        }

        if (options.initialize !== undefined && options.initialize !== null) {
          query.set('initialize', options.initialize ? '1' : '0')
        }

        if (options.convert_chunk_rows !== undefined && options.convert_chunk_rows !== null) {
          query.set('convert_chunk_rows', String(options.convert_chunk_rows))
        }
      }

      return await ApiService.get(`shop-user-stats?${query.toString()}`)
    } catch (error) {
      return error
    }
  },

  async downloadUserData(url) {
    return await ApiService.getFile(url, { skipErrorToast: true })
  },

  async downloadSearchUserData(queryString, format = 'xlsx') {
    const response = await ApiService.getFile(`search-user-data?${queryString}`, { skipErrorToast: true })
    const contentType = response.headers?.['content-type'] || ''

    if (contentType.includes('application/json')) {
      const err = await parseBlobJsonError(response)
      throw new Error(err?.message || 'Download failed')
    }

    if (!response?.data || !(response.data instanceof Blob) || response.data.size === 0) {
      throw new Error('Downloaded file is empty')
    }

    const fileName = `${buildDownloadTimestamp()}-search-user-data`
    const downloadResult = await FileDownloadService.downloadExportBlob(response.data, fileName, format, contentType)

    if (!downloadResult?.success) {
      throw new Error(downloadResult?.message || 'Failed to prepare export file')
    }

    return downloadResult
  }
}

export { StatService }
